import { Injectable, inject } from "@angular/core";
import { DecouplingBanner } from "@models/I_BannersDecoupling";
import { BannerTemplates, I_NewCarousel } from "@models/I_NewCarousel";
import { DecouplingUserServiceMSO } from "@providers/DecouplingUserServiceMSO.service";
import { UserService } from "@providers/UserService";
import { Observable, firstValueFrom, from, map, switchMap } from "rxjs";

enum BannersTypes {
  CASINO = "casino",
  SLOTS = "slots",
  OMNICHANNEL = "omnichannel",
  SPORTS = "sports",
  LIVECASINO = "livecasino",
}

@Injectable({
  providedIn: "root",
})
export class DecouplingBannersService extends DecouplingUserServiceMSO {
  private userService = inject(UserService);

  getBanners(bannerType: string): Observable<I_NewCarousel[]> {
    const url = `${this.globalVars.URL_BANNERS_DECOUPLING}/${this.getBannerTypeByLobby(bannerType)}`;

    return this.customGet<{ templates: DecouplingBanner[] }>(url, false).pipe(
      map((response) => this.mapResponseBanners(response.templates)),
      switchMap((mapedBanners) => from(mapedBanners)),
    );
  }

  private mapResponseBanners(banners: DecouplingBanner[]): Promise<I_NewCarousel[]> {
    return this.setOddsToBanners(
      banners
        .map<I_NewCarousel>((b) => {
          if (b.typeOfTemplate) {
            return b as I_NewCarousel;
          } else {
            const newBanner: I_NewCarousel = {
              typeOfTemplate: b.pageType,
              priority: b.priority,
              bannerDesktopImage: b.bannerDesktopImage,
              bannerMobileImage: b.bannerMobileImage,
              startDate: b.startDate,
              endDate: b.endDate,
              fromHour: b.fromHour,
              toHour: b.toHour,
              simpleImageTemplateOrTextLink: b.bannerLink,
              showLogged: b.showLogged,
              vipLevel: b.vipLevel,
            };
            return newBanner;
          }
        })
        .sort((a, b) => {
          if (a.priority > b.priority) {
            return 1;
          }
          if (a.priority < b.priority) {
            return -1;
          }
          return 0;
        }),
    );
  }

  private async setOddsToBanners(data: I_NewCarousel[]) {
    const bannersErrorCSV: Array<{ templates: string; key: number }> = [];
    await Promise.all(
      data.map(async (r, i) => {
        const canBetRequest: number = this.canRequestForOddAmount(r);
        if (canBetRequest) {
          try {
            if (r.typeOfTemplate === BannerTemplates.ONE_ODD) {
              const response = await firstValueFrom(this.userService.getEventMarketsByNode(canBetRequest));
              data[i].oneOddAmount = response.Odd;
            } else {
              const responseLeftTeam = await firstValueFrom(
                this.userService.getEventMarketsByNode(r.leftTeamBetKey),
              );
              data[i].leftTeamAmount = responseLeftTeam.Odd;
              if (r.typeOfTemplate === BannerTemplates.TEAMS_DRAW) {
                const responseDraw = await firstValueFrom(this.userService.getEventMarketsByNode(r.drawBetKey));
                data[i].drawAmount = responseDraw.Odd;
              }
              const responseRightTeam = await firstValueFrom(
                this.userService.getEventMarketsByNode(r.rightTeamBetKey),
              );
              data[i].rigthTeamAmount = responseRightTeam.Odd;
            }
          } catch (_) {
            bannersErrorCSV.push({ templates: r.typeOfTemplate, key: canBetRequest });
          }
        }
      }),
    );
    const auxdata: I_NewCarousel[] = data.filter((r) => {
      let isBad: boolean = false;
      bannersErrorCSV.forEach((b) => {
        if (b.templates === r.typeOfTemplate) {
          switch (r.typeOfTemplate) {
            case BannerTemplates.ONE_ODD:
            case BannerTemplates.TEAMS_DRAW:
            case BannerTemplates.TEAMS_NO_DRAW:
            case BannerTemplates.TENNIS:
              if (!isBad) {
                isBad = b.key === r.matchKey;
              }
              break;
          }
        }
      });
      return !isBad;
    });
    return auxdata;
  }

  private canRequestForOddAmount(slider: I_NewCarousel) {
    let eventId: number | undefined;
    if (
      slider.typeOfTemplate === BannerTemplates.TEAMS_DRAW ||
      slider.typeOfTemplate === BannerTemplates.TEAMS_NO_DRAW ||
      slider.typeOfTemplate === BannerTemplates.TENNIS ||
      slider.typeOfTemplate === BannerTemplates.ONE_ODD
    ) {
      eventId = slider.matchKey;
    }
    return eventId;
  }

  private getBannerTypeByLobby(lobby: string): string {
    const lobbyName: string = lobby.toLowerCase();
    if (lobbyName.includes("livecasino")) {
      return BannersTypes.LIVECASINO;
    }
    if (lobbyName.includes("casino")) {
      return BannersTypes.CASINO;
    }
    if (lobbyName.includes("slots")) {
      return BannersTypes.SLOTS;
    }
    if (lobbyName.includes("omnichannel")) {
      return BannersTypes.OMNICHANNEL;
    }
    return BannersTypes.SPORTS;
  }
}
